import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useLanguage } from "@/i18n/LanguageContext";
import { AlertTriangle, RefreshCw, Home, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ServerErrorProps {
  error?: Error;
  resetError?: () => void;
}

const ServerError = ({ resetError }: ServerErrorProps) => {
  const { language } = useLanguage();

  const handleRefresh = () => {
    if (resetError) {
      resetError();
    } else {
      window.location.reload();
    }
  };

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>500 - Server Error | Duelbonus.co</title>
        <meta name="description" content="Something went wrong on our end. We're working to fix it. Please try again or contact support." />
      </Helmet>
      
      <div className="min-h-screen bg-background flex flex-col">
        {/* Main Content */}
        <main className="flex-1 flex items-center justify-center px-4 py-16">
          <div className="max-w-lg w-full text-center">
            {/* Error Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-destructive/10 text-destructive text-sm font-medium mb-6">
              <AlertTriangle className="w-4 h-4" />
              Error 500
            </div>
            
            {/* Heading */}
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Something Went Wrong
            </h1>
            
            <p className="text-lg text-muted-foreground mb-8 max-w-md mx-auto">
              We're experiencing technical difficulties. Our team has been notified and is working on a fix.
            </p>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-10">
              <Button onClick={handleRefresh} variant="bonus" size="lg">
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to={`/${language}`}>
                  <Home className="w-4 h-4 mr-2" />
                  Go Home
                </Link>
              </Button>
            </div>

            {/* Help Box */}
            <div className="bg-card/50 rounded-xl p-6 border border-border text-left">
              <h2 className="text-sm font-semibold text-foreground mb-3">Need Help?</h2>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li className="flex items-start gap-3">
                  <RefreshCw className="w-4 h-4 mt-0.5 text-primary shrink-0" />
                  <span>Wait a moment and try refreshing the page</span>
                </li>
                <li className="flex items-start gap-3">
                  <Home className="w-4 h-4 mt-0.5 text-primary shrink-0" />
                  <span>Return to the homepage and navigate from there</span>
                </li>
                <li className="flex items-start gap-3">
                  <Mail className="w-4 h-4 mt-0.5 text-primary shrink-0" />
                  <span>
                    Contact us on{" "}
                    <a 
                      href="https://x.com/Duelbonus" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      X (Twitter)
                    </a>
                    {" "}if the problem persists
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="py-6 border-t border-border">
          <div className="container px-4 text-center text-xs text-muted-foreground">
            <p>© {new Date().getFullYear()} Duelbonus.co. All rights reserved.</p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default ServerError;
